"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.icpProfileFromCriteria = icpProfileFromCriteria;
const enums_1 = require("./enums");
const icp_1 = require("./icp");
const imports_1 = require("./imports");
const jev_1 = require("./jev");
const match_score_1 = require("./match-score");
/** §6.1 "Importance": Must have 5, Important 4, Nice to have 2. */
const IMPORTANCE_RATING = { must_have: 5, important: 4, nice_to_have: 2 };
/** OQ6 (a): a question from the application form says little about the work, so it never becomes a must-have. */
const FORM_QUESTION_RATING_MAX = 4;
const FORM_QUESTION_NOTE = 'Was a question in the application form — check it';
const UNFAIR_NOTE = 'Jev reads what a CV says about the work, never who a person is';
const DOMAIN_EVIDENCE = 'Real work in this area';
const DEFAULT_EVIDENCE = 'Shown in real work';
// Not "from the old criterion": the save schema refuses the word "old" (unfairAskKind, ADR 0050 §6), so the plan's first wording could not be saved.
const ENGLISH_EVIDENCE = 'Needed for this role (from the earlier screening criteria)';
const ENGLISH_WORD = /\benglish\b/i;
const HALF_YEARS_PER_YEAR = 1 / icp_1.ICP_YEARS_STEP;
/** Words that say "how long" or "what level" but not "of what": what is left over names a subject (§6.1 "also a skills line"). */
const FILLER_WORDS = new Set([
    ...['a', 'an', 'the', 'of', 'in', 'at', 'or', 'and', 'to', 'for', 'from', 'with', 's', 'is', 'must', 'have', 'any', 'plus'],
    ...['year', 'years', 'yr', 'yrs', 'experience', 'exp', 'work', 'working', 'total', 'relevant', 'professional', 'related'],
    ...['minimum', 'min', 'least', 'more', 'than', 'over', 'above', 'higher', 'required', 'preferred', 'level', 'equivalent', 'recognised', 'recognized'],
    ...['degree', 'degrees', 'graduate', 'graduation', 'completed', 'university', 'field', 'subject', 'discipline'],
]);
const FACT_ORDER = ['years', 'education', 'location'];
function cutAtWord(text, max) {
    const clean = text.replace(/\s+/g, ' ').trim();
    if (clean.length <= max)
        return clean;
    const cut = clean.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    const atWord = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
    return atWord.replace(/[\s,;:]+$/, '');
}
function educationParamOf(criterion) {
    if (criterion.type !== 'education_level')
        return null;
    const value = criterion.params['min_level'];
    return enums_1.EDUCATION_LEVELS.find((level) => level === value) ?? null;
}
function numberParamOf(criterion, key) {
    const value = criterion.params[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function placeParamOf(criterion) {
    const city = criterion.params['city'];
    return typeof city === 'string' && city.trim() !== '' ? city.trim() : criterion.label;
}
function hasSubjectBeyondFact(label) {
    return (0, imports_1.normaliseHeader)(label)
        .split(' ')
        .some((word) => word !== '' && !/^\d+$/.test(word) && !FILLER_WORDS.has(word) && (0, imports_1.educationLevelOf)(word) === null);
}
function lookForEvidenceOf(criterion) {
    if (criterion.lookFor.length === 0 && criterion.partlyCounts.length === 0)
        return DEFAULT_EVIDENCE;
    const look = criterion.lookFor.length > 0 ? criterion.lookFor.join(', ') : criterion.label;
    const partly = criterion.partlyCounts.length > 0 ? `; partly: ${criterion.partlyCounts.join(', ')}` : '';
    return cutAtWord(`Look for: ${look}${partly}`, icp_1.ICP_EVIDENCE_MAX);
}
/** The schema's own fact type holds a mutable alias list. */
function inputFactOf(fact) {
    return fact.type === 'location' ? { ...fact, aliases: [...fact.aliases] } : fact;
}
function yearsFactOf(criterion) {
    const minimum = criterion.minLevel ?? numberParamOf(criterion, 'min_years');
    if (minimum === null || minimum <= 0)
        return null;
    const minYears = Math.min(icp_1.ICP_YEARS_MAX, Math.ceil(minimum * HALF_YEARS_PER_YEAR) / HALF_YEARS_PER_YEAR);
    return { type: 'years', minYears, maxYears: null };
}
function locationFactOf(criterion) {
    const city = cutAtWord(placeParamOf(criterion), icp_1.ICP_CITY_MAX);
    const spellings = criterion.lookFor.map((word) => cutAtWord(word, icp_1.ICP_CITY_MAX)).filter((word) => word !== '');
    return { type: 'location', city, aliases: (0, icp_1.icpAliasesOf)(city, spellings).slice(0, icp_1.ICP_ALIASES_MAX) };
}
/**
 * The mapping table of §6.1, pure and deterministic: `idOf(key, part)` returns the line id for a criterion key and part
 * (`'main'`, `'skills'`, `'fact'`…), so running it again gives the same ids. Importance becomes the rating (Must have 5,
 * Important 4, Nice to have 2); form-question kinds (Yes/No, Level that are not a degree or English) are rated at most 4 (OQ6 a).
 */
function icpProfileFromCriteria(criteria, job, idOf) {
    const notes = [];
    const skills = [];
    const english = [];
    const facts = [];
    const skillOf = (criterion, part, rating, evidence) => {
        const text = cutAtWord(criterion.label, icp_1.ICP_TEXT_MAX);
        if (text.length < jev_1.JEV_PART_MIN_LENGTH) {
            notes.push(`"${criterion.label}" was left out: its title is too short for Jev to read.`);
            return;
        }
        if ((0, jev_1.unfairAskKind)(text)) {
            notes.push(`"${criterion.label}" was left out: ${UNFAIR_NOTE}.`);
            return;
        }
        const safeEvidence = (0, jev_1.unfairAskKind)(evidence) ? DEFAULT_EVIDENCE : evidence;
        skills.push({ criterionLabel: criterion.label, line: { id: idOf(criterion.key, part), kind: 'jev', group: 'skills', text, evidence: safeEvidence, rating, source: 'job' } });
    };
    for (const criterion of criteria) {
        const rating = IMPORTANCE_RATING[criterion.importance];
        const degree = (0, match_score_1.degreeLevelOf)(criterion) ?? educationParamOf(criterion);
        if (degree) {
            facts.push({ criterion, rating, fact: { type: 'education', minLevel: degree }, strictness: enums_1.EDUCATION_LEVELS.indexOf(degree), hasSubject: hasSubjectBeyondFact(criterion.label) });
            continue;
        }
        if (ENGLISH_WORD.test(criterion.label)) {
            english.push({ criterion, rating });
            continue;
        }
        if (criterion.kind === 'years') {
            const fact = yearsFactOf(criterion);
            if (fact === null)
                notes.push(`"${criterion.label}" was left out: it did not ask for a minimum number of years.`);
            else
                facts.push({ criterion, rating, fact, strictness: fact.minYears, hasSubject: hasSubjectBeyondFact(criterion.label) });
            continue;
        }
        if (criterion.type === 'location') {
            if (job.isRemote)
                notes.push(`"${criterion.label}" was left out: the job is remote.`);
            else
                facts.push({ criterion, rating, fact: locationFactOf(criterion), strictness: 0, hasSubject: false });
            continue;
        }
        if (criterion.kind === 'yes_no' || criterion.kind === 'level') {
            skillOf(criterion, 'main', rating > FORM_QUESTION_RATING_MAX ? FORM_QUESTION_RATING_MAX : rating, lookForEvidenceOf(criterion));
            notes.push(`"${criterion.label}": ${FORM_QUESTION_NOTE}.`);
            continue;
        }
        skillOf(criterion, 'main', rating, lookForEvidenceOf(criterion));
    }
    const factLines = [];
    for (const type of FACT_ORDER) {
        const ofType = facts.filter((candidate) => candidate.fact.type === type);
        // Highest rated wins, then the stricter fact, then criteria order (Array.sort is stable).
        const [winner, ...rest] = [...ofType].sort((a, b) => b.rating - a.rating || b.strictness - a.strictness);
        if (!winner)
            continue;
        factLines.push({ id: idOf(winner.criterion.key, 'fact'), kind: 'fact', group: 'facts', fact: inputFactOf(winner.fact), rating: winner.rating, source: 'job' });
        if (winner.hasSubject)
            skillOf(winner.criterion, 'skills', winner.rating, winner.fact.type === 'years' ? DOMAIN_EVIDENCE : lookForEvidenceOf(winner.criterion));
        for (const loser of rest) {
            skillOf(loser.criterion, 'main', loser.rating, loser.fact.type === 'years' ? DOMAIN_EVIDENCE : lookForEvidenceOf(loser.criterion));
            notes.push(`"${loser.criterion.label}": only one ${type} fact fits a profile, so it became a line Jev reads.`);
        }
    }
    return { items: [...keptSkillLines(skills, notes), ...englishLineOf(english, notes, idOf), ...factLines], notes };
}
/** Jev reads up to ten lines: the ten highest rated stay (ties: criteria order), in criteria order. */
function keptSkillLines(skills, notes) {
    if (skills.length <= icp_1.ICP_JEV_ITEMS_MAX)
        return skills.map((skill) => skill.line);
    const kept = new Set(skills
        .map((skill, index) => ({ skill, index }))
        .sort((a, b) => b.skill.line.rating - a.skill.line.rating || a.index - b.index)
        .slice(0, icp_1.ICP_JEV_ITEMS_MAX)
        .map((entry) => entry.skill));
    const dropped = skills.filter((skill) => !kept.has(skill)).map((skill) => `"${skill.criterionLabel}"`);
    notes.push(`Jev reads up to ${icp_1.ICP_JEV_ITEMS_MAX} lines, so ${dropped.join(', ')} ${dropped.length === 1 ? 'was' : 'were'} left out.`);
    return skills.filter((skill) => kept.has(skill)).map((skill) => skill.line);
}
/** A profile has one English line: the highest rated criterion about English; the rest are left out with a note. */
function englishLineOf(candidates, notes, idOf) {
    const [first, ...rest] = [...candidates].sort((a, b) => b.rating - a.rating);
    for (const other of rest)
        notes.push(`"${other.criterion.label}" was left out: a profile has one English line.`);
    if (!first)
        return [];
    const text = cutAtWord(first.criterion.label, icp_1.ICP_TEXT_MAX);
    if (text.length < jev_1.JEV_PART_MIN_LENGTH || (0, jev_1.unfairAskKind)(text)) {
        notes.push(`"${first.criterion.label}" was left out: ${UNFAIR_NOTE}.`);
        return [];
    }
    return [{ id: idOf(first.criterion.key, 'main'), kind: 'english', group: 'communication', text, evidence: ENGLISH_EVIDENCE, rating: first.rating, source: 'job' }];
}
//# sourceMappingURL=icp-migration.js.map